import { v4 as uuid } from "uuid";

// this may seem redundant, but this allows for a performance gain
// with easier event unbinding when a component is destroyed
window.vesl = window.vesl ?? {};

function EventBus($logger, $rootScope, appState, $timeout) {
	const service = this;
	const activeListeners = new Map();
	const buffer = new Map();

	service.register = register;
	service.trigger = trigger;
	service.unregister = unregister;

	function register(event, callback, scope, options) {
		const hadListeners = getListenerCount(event) > 0;
		const registration = createRegistration(event, callback, scope, options);
		const angularUnbinder = $rootScope.$on(event, (angularEvent, payload) => {
			dispatchRegistration(registration, angularEvent, payload);
		});
		registration.cleanup.add(angularUnbinder);
		incrementListenerCount(event);

		const unbinder = () => disposeRegistration(registration);

		if(scope) {
			registration.cleanup.add(scope.$on("$destroy", unbinder));
		} else {
			$logger.warn("An event was loaded without a scope.  If unintentional, this could lead to ghost handlers", { event: event });
		}
		registration.cleanup.add(() => stopWaitingForReadiness(registration));

		if(!hadListeners) {
			flushBuffer(event);
		}

		return unbinder;
	}

	function createRegistration(event, callback, scope, options) {
		validateRegistrationOptions(options);

		return {
			id: uuid(),
			event,
			callback,
			scope,
			options,
			hasReadiness: hasReadinessOption(options),
			forceAfterMs: options?.forceAfterMs,
			isActive: true,
			isFlushing: false,
			cleanup: createCleanupGroup(event, "registration"),
			watcher: undefined,
			didLogReadinessFailure: false,
			queue: []
		};
	}

	function createCleanupGroup(event, name) {
		let callbacks = [];
		let isDisposed = false;

		return {
			add(callback) {
				if(typeof callback !== "function") {
					throw new TypeError("Event registration cleanup callback must be a function.");
				}

				if(isDisposed) {
					invokeCleanup(callback, event, name);
					return;
				}

				callbacks.push(callback);
			},
			dispose() {
				if(isDisposed) {
					return;
				}

				isDisposed = true;
				const pending = callbacks.reverse();
				callbacks = [];
				pending.forEach(callback => invokeCleanup(callback, event, name));
			}
		};
	}

	function invokeCleanup(callback, event, cleanupGroup) {
		try {
			callback();
		}
		catch(error) {
			$logger.exception(error, "Event registration cleanup failed", {
				event,
				cleanupGroup
			});
		}
	}

	function disposeRegistration(registration) {
		if(!registration.isActive) {
			return;
		}

		registration.isActive = false;
		decrementListenerCount(registration.event);
		registration.cleanup.dispose();
		registration.queue = [];
	}

	function validateRegistrationOptions(options) {
		if(options === undefined || options === null) {
			return;
		}

		if(typeof options !== "object" && typeof options !== "function") {
			throw new TypeError("Event registration options must be an object.");
		}

		if(options.forceAfterMs !== undefined && (!Number.isFinite(options.forceAfterMs) || options.forceAfterMs < 0)) {
			throw new TypeError("Event registration forceAfterMs must be a non-negative finite number.");
		}

		if(options.forceAfterMs !== undefined && !hasReadinessOption(options)) {
			throw new TypeError("Event registration forceAfterMs requires an isReady property.");
		}
	}

	function hasReadinessOption(options) {
		return options !== undefined && options !== null && "isReady" in options;
	}

	function unregister(unbinder) {
		if(unbinder) {
			unbinder();
		}
	}

	function dispatchRegistration(registration, angularEvent, payload) {
		if(!registration.isActive) {
			return;
		}

		if(registration.isFlushing) {
			queueRegistrationDelivery(registration, angularEvent, payload, false);
			return;
		}

		if(registration.queue.length > 0) {
			queueRegistrationDelivery(registration, angularEvent, payload, false);

			if(isRegistrationReady(registration)) {
				flushRegistration(registration, "ready");
			}
			else {
				startWaitingForReadiness(registration);
			}
			return;
		}

		if(isRegistrationReady(registration)) {
			invokeRegistration(registration, angularEvent, payload, {
				mode: "immediate",
				forcedByTimeout: false,
				queuedForMs: 0
			});
			return;
		}

		queueRegistrationDelivery(registration, angularEvent, payload);
	}

	function isRegistrationReady(registration) {
		if(!registration.hasReadiness) {
			return true;
		}

		try {
			const isReady = registration.options.isReady === true;
			registration.didLogReadinessFailure = false;

			return isReady;
		}
		catch(error) {
			if(!registration.didLogReadinessFailure) {
				registration.didLogReadinessFailure = true;
				$logger.exception(error, "Event registration readiness check failed", {
					event: registration.event
				});
			}

			return false;
		}
	}

	function queueRegistrationDelivery(registration, angularEvent, payload, startWaiting = true) {
		registration.queue.push({
			angularEvent,
			payload,
			queuedAt: Date.now()
		});

		if(startWaiting) {
			startWaitingForReadiness(registration);
		}
	}

	function startWaitingForReadiness(registration) {
		if(!registration.isActive || registration.queue.length === 0) {
			return;
		}

		if(!registration.watcher) {
			const watcher = createCleanupGroup(registration.event, "readiness watcher");
			registration.watcher = watcher;
			const watchScope = registration.scope || $rootScope;
			watcher.add(watchScope.$watch(
				() => isRegistrationReady(registration),
				isReady => {
					if(isReady && registration.watcher === watcher) {
						flushRegistration(registration, "ready");
					}
				}
			));

			if(registration.forceAfterMs !== undefined) {
				let isTimeoutPending = true;
				const timeoutPromise = $timeout(() => {
					isTimeoutPending = false;

					if(registration.watcher !== watcher) {
						return;
					}

					if(!registration.isActive || registration.queue.length === 0) {
						stopWaitingForReadiness(registration);
						return;
					}

					if(isRegistrationReady(registration)) {
						flushRegistration(registration, "ready");
						return;
					}

					const waitedMs = Date.now() - registration.queue[0].queuedAt;
					$logger.warn("Event handler forced before registration became ready", {
						event: registration.event,
						queuedCount: registration.queue.length,
						waitedMs
					});
					flushRegistration(registration, "timeout");
				}, registration.forceAfterMs);
				watcher.add(() => {
					if(isTimeoutPending) {
						isTimeoutPending = false;
						$timeout.cancel(timeoutPromise);
					}
				});
			}
		}
	}

	function stopWaitingForReadiness(registration) {
		if(registration.watcher) {
			const watcher = registration.watcher;
			registration.watcher = undefined;
			watcher.dispose();
		}
	}

	function flushRegistration(registration, mode) {
		if(!registration.isActive || registration.queue.length === 0) {
			return;
		}

		stopWaitingForReadiness(registration);
		const queued = registration.queue;
		registration.queue = [];
		registration.isFlushing = true;

		try {
			queued.forEach(delivery => {
				invokeRegistration(registration, delivery.angularEvent, delivery.payload, {
					mode,
					forcedByTimeout: mode === "timeout",
					queuedForMs: Date.now() - delivery.queuedAt
				});
			});
		}
		finally {
			registration.isFlushing = false;
		}

		if(registration.queue.length > 0) {
			if(isRegistrationReady(registration)) {
				flushRegistration(registration, "ready");
			}
			else {
				startWaitingForReadiness(registration);
			}
		}
	}

	function invokeRegistration(registration, angularEvent, payload, delivery) {
		if(!registration.isActive) {
			return;
		}

		recordDelayedDelivery(registration, delivery);

		try {
			const result = registration.callback.call(null, angularEvent, payload);

			if(result && typeof result.then === "function") {
				Promise.resolve(result).catch(error => recordInvocationFailure(error, registration, delivery));
			}
		}
		catch(error) {
			recordInvocationFailure(error, registration, delivery);
		}
	}

	function recordDelayedDelivery(registration, delivery) {
		if(delivery.mode === "immediate") {
			return;
		}

		const message = delivery.forcedByTimeout
			? "Queued event handler forced after readiness timeout elapsed"
			: "Queued event handler invoked once registration became ready";

		$logger.info(message, {
			event: registration.event,
			deliveryMode: delivery.mode,
			forcedByTimeout: delivery.forcedByTimeout,
			queuedForMs: delivery.queuedForMs
		});
	}

	function recordInvocationFailure(error, registration, delivery) {
		$logger.exception(error, "Event handler invocation failed", {
			event: registration.event,
			deliveryMode: delivery.mode,
			forcedByTimeout: delivery.forcedByTimeout,
			queuedForMs: delivery.queuedForMs
		});
	}

	function trigger(event, payload = {}) {
		if(window.vesl.logEvents !== undefined) {
			if(!Array.isArray(window.vesl.logEvents) || (window.vesl.logEvents.indexOf(event) > -1)) {
				$logger.debug(`Event triggered: ${event}`, payload);
			}
		}

		if(getListenerCount(event) > 0) {
			emitWhenSafe(event, payload);
		}
		else if(shouldBuffer(event, payload)) {
			bufferEvent(event, payload);
		}
	}

	function shouldBuffer(event) {
		const field = getFieldFromEvent(event);

		if(field?.IsDetail === true) {
			return false;
		}

		return true;
	}

	function getFieldFromEvent(event) {
		const [, eventFieldId] = event.split(":::");

		if(eventFieldId === undefined) {
			return undefined;
		}

		return appState.forms.selected.Fields.find(f => f.Id === Number(eventFieldId.trim()));
	}

	function emitWhenSafe(event, payload) {
		if($rootScope.$$phase) {
			$timeout(() => {
				$rootScope.$emit(event, payload);
			}, 0);
			return;
		}

		$rootScope.$emit(event, payload);
	}

	function bufferEvent(event, payload) {
		if(!buffer.has(event)) {
			buffer.set(event, []);
		}
		buffer.get(event).push(payload);
	}

	function incrementListenerCount(event) {
		activeListeners.set(event, getListenerCount(event) + 1);
	}

	function decrementListenerCount(event) {
		const nextCount = getListenerCount(event) - 1;

		if(nextCount > 0) {
			activeListeners.set(event, nextCount);
			return;
		}

		activeListeners.delete(event);
	}

	function getListenerCount(event) {
		return activeListeners.get(event) || 0;
	}

	function flushBuffer(event) {
		if(buffer.has(event)) {
			const queued = buffer.get(event);
			buffer.delete(event);
			queued.forEach(payload => {
				$rootScope.$emit(event, payload);
			});
		}
	}

	return service;
}

export default angular
	.module("vesl.services.event-bus", [])
	.service("eventBus", EventBus).name;
